import classNames from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Errors } from './Errors';
import { useState } from 'react';
import { SuccessCheck } from './SuccessCheck';
import CountrySelector from './CountrySelector';
import { trackSubmitForm } from '@/lib/utilities/gtm';
import { PrivacyLabel } from '../Cookies/PrivacyLabel';
import ManufacturerSelector from './ManufacturerSelector';
import { omitBy } from 'lodash-es';
import { setErrorOn400 } from './lib/setErrorOn400';

interface IFormInput {
  firstName: string;
  lastName: string;
  manufacturer: string;
  model: string;
  year: string;
  type: string;
  description: string;
  machineCountry: string;
  email: string;
  phone: string;
  companyName: string;
  country: string;
  terms: boolean;
  comment: string;
  leadPlatform: string;
}

export function SellForm() {
  const { t } = useTranslation('common');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [page, setPage] = useState(1);
  const LAST_PAGE = 3;
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    control,
    setError,
  } = useForm<IFormInput>({
    mode: 'all',
  });
  const onSubmit: SubmitHandler<IFormInput> = (data) => {
    setIsSubmitted(true);
    data.leadPlatform = 'BEVMAQ Website';
    fetch(process.env.NEXT_PUBLIC_CRM_API_URL + '/selllead', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(omitBy(data, (value) => value === '')),
    })
      .then(setErrorOn400(setError))
      .then((data) => {
        console.log('Success:', data);
        trackSubmitForm({
          action: 'Sell',
          category: 'Sell Form',
          label: data.model as string,
        });
      })
      .catch((error) => {
        console.log('error on POST /buy lead', error);
        setIsSubmitted(false);
      });
  };
  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className='space-y-8 rounded '
      data-testid='sell-form'
      noValidate
    >
      {!isSubmitted ? (
        <>
          {page >= 1 && (
            <div className={page > 1 ? 'hidden' : 'space-y-8'}>
              <ManufacturerSelector
                label={t('Manufacturer')}
                name={'manufacturer'}
                error={errors.manufacturer}
                register={register}
                control={control}
                containerClasses='col-span-12 md:col-span-6'
              />
              <div className='space-y-4 md:flex md:space-x-4 md:space-y-0'>
                <div className='flex-grow'>
                  <label className='input-label' htmlFor='model'>
                    {t('Model')} *
                  </label>
                  <input
                    className={'input-field'}
                    id={'model'}
                    type='text'
                    placeholder={t('per example') + ' Volumetric S-60'}
                    {...register('model', {
                      required: t('Required'),
                      min: 1970,
                      maxLength: 2022,
                    })}
                  />
                  <Errors data={errors.model} />
                </div>
                <div>
                  <label className='input-label' htmlFor='year'>
                    {t('Year')} *
                  </label>
                  <input
                    className={'input-field'}
                    id={'year'}
                    type='number'
                    placeholder={t('per example') + ' 2005'}
                    min={1950}
                    {...register('year', {
                      required: t('Required'),
                      min: 1900,
                      max: 2022,
                    })}
                  />
                  <Errors data={errors.year} />
                </div>
              </div>
            </div>
          )}
          {page >= 2 && (
            <div className={page > 2 ? 'hidden' : 'space-y-4'}>
              <div>
                <label className='input-label' htmlFor='type'>
                  {t('Machine Type')}
                </label>
                <input
                  className={'input-field'}
                  id={'type'}
                  type='text'
                  placeholder={
                    t('per example') + ' ' + t('sell.eg.machine type')
                  }
                  {...register('type')}
                />
              </div>
              <div>
                <label className='input-label' htmlFor='description'>
                  {t('Description')}
                </label>
                <input
                  className={'input-field'}
                  id={'description'}
                  type='text'
                  placeholder={
                    t('per example') + ' ' + t('sell.eg.description')
                  }
                  {...register('description', {
                    maxLength: 1500,
                  })}
                />
              </div>
              <CountrySelector
                label={t('Machines Country')}
                name={'machineCountry'}
                error={errors.machineCountry}
                register={register}
                control={control}
                containerClasses='col-span-12 md:col-span-6'
              />
            </div>
          )}
          {page >= 3 && (
            <div className={page > 3 ? 'hidden' : 'space-y-4'}>
              <div className='space-y-4 md:flex md:space-x-4 md:space-y-0'>
                <div className='flex-grow'>
                  <label className='input-label' htmlFor='firstName'>
                    {t('First Name')} *
                  </label>
                  <input
                    className={'input-field'}
                    id={'firstName'}
                    type='text'
                    {...register('firstName', {
                      required: t('Required'),
                    })}
                  />
                  <Errors data={errors.firstName} />
                </div>
                <div className='flex-grow'>
                  <label className='input-label' htmlFor='lastName'>
                    {t('Last Name')} *
                  </label>
                  <input
                    className={'input-field'}
                    id={'lastName'}
                    type='text'
                    {...register('lastName', {
                      required: t('Required'),
                    })}
                  />
                  <Errors data={errors.lastName} />
                </div>
              </div>
              <div className='space-y-4 md:flex md:space-x-4 md:space-y-0'>
                <div className='flex-grow'>
                  <label className='input-label' htmlFor='email'>
                    {t('Email')} *
                  </label>
                  <input
                    className={'input-field'}
                    id={'email'}
                    type='email'
                    placeholder={'example@gmail.com'}
                    {...register('email', {
                      required: t('Required'),
                    })}
                  />
                  <Errors data={errors.email} />
                </div>
                <div>
                  <label className='input-label' htmlFor='phone'>
                    {t('Phone')}
                  </label>
                  <input
                    className={'input-field'}
                    id={'phone'}
                    type='text'
                    placeholder={t('sell.eg.phone')}
                    {...register('phone', {
                      required: t('Required'),
                    })}
                  />
                  <Errors data={errors.email} />
                </div>
              </div>
              <div>
                <label className='input-label' htmlFor='companyName'>
                  {t('Company Name')}
                </label>
                <input
                  className={'input-field'}
                  id={'companyName'}
                  type='text'
                  {...register('companyName')}
                />
              </div>
              <CountrySelector
                name={'country'}
                error={errors.country}
                register={register}
                control={control}
                containerClasses='col-span-12 md:col-span-6'
              />
              <div>
                <label className='input-label' htmlFor='model'>
                  {t('Comment')}
                </label>
                <textarea
                  className={'input-field'}
                  id={'comment'}
                  {...register('comment')}
                />
              </div>
              <div className='form-check mb-4'>
                <input
                  className='float-left mr-2 mt-1 h-4 w-4 cursor-pointer appearance-none rounded-sm border border-gray-300 bg-white bg-contain bg-center bg-no-repeat align-top transition duration-200 checked:border-blue-600 checked:bg-blue-600 focus:outline-none'
                  type='checkbox'
                  id='terms'
                  {...register('terms', {
                    required: t('Required'),
                  })}
                />
                <label className='text-gray-800' htmlFor='terms'>
                  <PrivacyLabel />
                </label>
              </div>
              <button
                type='submit'
                className={classNames({
                  'h-16 w-full rounded px-4 py-2 text-white ': true,
                  'bg-blue-500 hover:bg-blue-600': isValid,
                  'bg-gray-500': !isValid,
                })}
              >
                <h3 className='text-xl'>{t('Send')}</h3>
              </button>
            </div>
          )}
          {page != LAST_PAGE && (
            <button
              type='button'
              className={classNames({
                'h-16 w-full rounded px-4 py-2 text-white transition duration-200':
                  true,
                'bg-blue-500 hover:bg-blue-600': isValid,
                'bg-gray-500': !isValid,
              })}
              onClick={() => isValid && setPage(page + 1)}
            >
              <h3 className='text-xl'>{t('common:Next')}</h3>
            </button>
          )}
          <progress
            className='progress progress-primary w-full bg-gray-200'
            value={(page / LAST_PAGE) * 100}
            max={100}
          />
        </>
      ) : (
        <SuccessCheck
          message={t('common:thanks-we-will-contact-you-as-soon-as-possible')}
        />
      )}
    </form>
  );
}
